import React, { useState, createContext, useContext, ReactNode } from 'react';
import { CalendlyPopup } from '../components/CalendlyPopup';
import { ImmediateConsultationModal } from '../components/ImmediateConsultationModal';

interface CalendlyContextType {
  openCalendly: () => void;
  closeCalendly: () => void;
  isOpen: boolean;
}

const CalendlyContext = createContext<CalendlyContextType | undefined>(undefined);

export const useCalendlyPopup = () => {
  const context = useContext(CalendlyContext);
  if (!context) {
    throw new Error('useCalendlyPopup must be used within a CalendlyProvider');
  }
  return context;
};

interface CalendlyProviderProps {
  children: ReactNode;
}

export const CalendlyProvider: React.FC<CalendlyProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showImmediateConsult, setShowImmediateConsult] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState<string | undefined>();

  const openCalendly = () => setIsOpen(true);
  const closeCalendly = () => setIsOpen(false);

  const handleScheduled = (event: any) => {
    // Extract appointment date if available from the event
    const eventDate = event?.payload?.invitee?.scheduled_event?.start_time;
    if (eventDate) {
      const date = new Date(eventDate);
      setAppointmentDate(date.toLocaleString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }));
    }
    // Show the immediate consultation modal
    setShowImmediateConsult(true);
  };

  const closeImmediateConsult = () => {
    setShowImmediateConsult(false);
    setAppointmentDate(undefined);
  };

  return (
    <CalendlyContext.Provider value={{ openCalendly, closeCalendly, isOpen }}>
      {children}
      <CalendlyPopup 
        isOpen={isOpen} 
        onClose={closeCalendly}
        onScheduled={handleScheduled}
      />
      <ImmediateConsultationModal 
        isOpen={showImmediateConsult}
        onClose={closeImmediateConsult}
        appointmentDate={appointmentDate}
      />
    </CalendlyContext.Provider>
  );
};